import type { Framework } from '@toeverything/infra';

import { AuthProvider } from '../provider/auth';
import { ServerScope } from '../scopes/server';
import { FetchService } from '../services/fetch';

const CSRF_COOKIE_NAME = 'affine_csrf_token';

function getCookieValue(name: string) {
  if (typeof document === 'undefined') {
    return null;
  }

  const cookies = document.cookie ? document.cookie.split('; ') : [];
  for (const cookie of cookies) {
    const idx = cookie.indexOf('=');
    const key = idx === -1 ? cookie : cookie.slice(0, idx);
    if (key === name) {
      return idx === -1 ? '' : cookie.slice(idx + 1);
    }
  }
  return null;
}

export function configureDefaultAuthProvider(framework: Framework) {
  framework.scope(ServerScope).override(AuthProvider, resolver => {
    const fetchService = resolver.get(FetchService);
    return {
      async signInMagicLink(
        email: string,
        token: string,
        clientNonce?: string
      ) {
        await fetchService.fetch('/api/auth/magic-link', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email, token, client_nonce: clientNonce }),
        });
      },

      async signInOauth(
        code: string,
        state: string,
        _provider: string,
        clientNonce?: string
      ) {
        const res = await fetchService.fetch('/api/oauth/callback', {
          method: 'POST',
          body: JSON.stringify({ code, state, client_nonce: clientNonce }),
          headers: {
            'content-type': 'application/json',
          },
        });
        return await res.json();
      },
      async signInPassword(credential: {
        email: string;
        password: string;
        verifyToken?: string;
        challenge?: string;
      }) {
        const headers: Record<string, string> = {};

        if (credential.verifyToken) {
          headers['x-captcha-token'] = credential.verifyToken;
        }
        if (credential.challenge) {
          headers['x-captcha-challenge'] = credential.challenge;
        }

        await fetchService.fetch('/api/auth/sign-in', {
          method: 'POST',
          body: JSON.stringify(credential),
          headers: {
            'content-type': 'application/json',
            ...headers,
          },
        });
      },
      async signOut() {
        const csrfToken = getCookieValue(CSRF_COOKIE_NAME);
        const res = await fetchService.fetch('/api/auth/sign-out', {
          method: 'POST',
          headers: csrfToken ? { 'x-affine-csrf-token': csrfToken } : undefined,
        });
        // PIPE: complete the OIDC RP-Initiated Logout. The backend returns Keycloak's end_session URL
        // (with id_token_hint); redirecting the browser there terminates the IdP SSO session and clears
        // its HttpOnly cookie — otherwise the next "Continue with OIDC" silently re-authenticates the
        // same user (a shared-device leak, and it blocks switching accounts). Local sign-out is already
        // done server-side, so a missing/!ok response just degrades to local-only logout.
        try {
          const body = await res.json();
          if (body?.logoutUrl) {
            window.location.href = body.logoutUrl;
          }
        } catch {
          // no logout URL in the response — the local sign-out already completed.
        }
      },
    };
  });
}
